import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { PropTypes } from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { formatRoute } from 'react-router-named-routes';
import RecipeCollectionsAddMealCard from './recipes_collections_add_meal_card';
import { END_USER_RECIPE_SINGLE_COLLECTION_ROUTE } from '../../services/routes';
import {
  useCreateMealMutation,
  useGetDistilledMealsByParentsQuery,
  useGetSharedBoardsByIDsQuery,
  useGetDistilledSharedMealsByParentsQuery,
  useUpdateSmorgBoardMutation,
  useLazyGetFullMealQuery,
  useLazyGetFullSharedMealQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  parentIDOfMealID,
  parentIDOfSharedMealID,
} from '../../services/smorg_board';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetDistilledMealByIDAndParentQuery,
  useGetHealthProBoardsQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  copyMealToBoard,
  copySharedMealToBoard,
} from '../../operations/recipes_operations';
import { recipeCollectionsCardDOMID } from '../../services/meals';
import { useTracker } from '../../operations/tracking_operations';
import { SHARED_BOARDS_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';

const RecipeCollectionsAddMealItem = ({
  mealID,
  destinationBoardID,
  destinationMenuID,
  onClick,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tracker = useTracker();

  const { data: userProfile } = useGetUserProfileQuery();

  const [updateSmorgBoard] = useUpdateSmorgBoardMutation();

  const [createMeal] = useCreateMealMutation();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const toRecipesBoard = (ownedRecipesBoards || []).find(
    (board) => board.id === destinationBoardID,
  );

  const parentIDInMeals = parentIDOfMealID(mealID, ownedRecipesBoards);

  const {
    data: spaceAssignedSharedRecipesBoardsIDs,
    isLoading: isSaceAssignedSharedRecipesBoardsIDsLoading,
  } = useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery();

  const { data: healthProBoards } = useGetHealthProBoardsQuery();

  const { data: sharedBoards } = useGetSharedBoardsByIDsQuery(
    spaceAssignedSharedRecipesBoardsIDs,
    {
      skip:
        isSaceAssignedSharedRecipesBoardsIDsLoading ||
        !spaceAssignedSharedRecipesBoardsIDs ||
        spaceAssignedSharedRecipesBoardsIDs.length === 0,
      refetchOnMountOrArgChange: SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
    },
  );

  const healthProBoardIDs = useMemo(
    () => (healthProBoards || []).map((b) => b.id),
    [healthProBoards],
  );

  const sharedBoardIDs = useMemo(
    () => (sharedBoards || []).map((b) => b.id),
    [sharedBoards],
  );

  const parentIDInHealthProMeals = parentIDOfMealID(mealID, healthProBoards);

  const parentIDInSharedMeals = parentIDOfSharedMealID(mealID, sharedBoards);

  const { data: distilledMealInMeals } = useGetDistilledMealByIDAndParentQuery(
    { id: mealID, parentID: parentIDInMeals },
    { skip: !mealID || !parentIDInMeals },
  );

  const { data: distilledHealthProMealsByParents } =
    useGetDistilledMealsByParentsQuery(healthProBoardIDs, {
      skip: !healthProBoardIDs || healthProBoardIDs.length === 0,
    });

  const { data: distilledSharedMealsByParents } =
    useGetDistilledSharedMealsByParentsQuery(sharedBoardIDs, {
      skip: !sharedBoardIDs || sharedBoardIDs.length === 0,
    });

  const distilledMealInHealthProMeals = ((distilledHealthProMealsByParents ||
    {})[parentIDInHealthProMeals] || {})[mealID];

  const distilledMealInSharedMeals = ((distilledSharedMealsByParents || {})[
    parentIDInSharedMeals
  ] || {})[mealID];

  const isSharedMeal = !!distilledMealInSharedMeals;

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetSharedMeal] = useLazyGetFullSharedMealQuery();

  const distilledMeal =
    mealID &&
    (distilledMealInMeals ||
      distilledMealInHealthProMeals ||
      distilledMealInSharedMeals);

  const onAdd = useCallback(async () => {
    const cb = (copiedMeal) => {
      window.setTimeout(() => {
        const el = document.getElementById(
          recipeCollectionsCardDOMID(copiedMeal.id),
        );
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 200);
    };

    if (isSharedMeal) {
      const { data: fullSharedMeal } = await lazyGetSharedMeal(mealID);
      await copySharedMealToBoard(
        fullSharedMeal,
        toRecipesBoard,
        destinationMenuID,
        currentSpaceMembership,
        updateSmorgBoard,
        createMeal,
        userProfile,
        tracker,
        null,
        cb,
        dispatch,
      );
    } else {
      const { data: fullMeal } = await lazyGetMeal(mealID);
      await copyMealToBoard(
        fullMeal,
        toRecipesBoard,
        destinationMenuID,
        currentSpaceMembership,
        updateSmorgBoard,
        createMeal,
        userProfile,
        tracker,
        null,
        cb,
        dispatch,
        null,
      );
    }
    navigate(
      formatRoute(END_USER_RECIPE_SINGLE_COLLECTION_ROUTE, {
        boardID: destinationBoardID,
      }),
    );
  }, [
    isSharedMeal,
    navigate,
    destinationBoardID,
    lazyGetSharedMeal,
    mealID,
    toRecipesBoard,
    destinationMenuID,
    currentSpaceMembership,
    updateSmorgBoard,
    createMeal,
    userProfile,
    tracker,
    dispatch,
    lazyGetMeal,
  ]);

  if (!distilledMeal) {
    return null;
  }

  return (
    <RecipeCollectionsAddMealCard
      title={distilledMeal.recipes[0].title}
      imageUrl={distilledMeal.recipes[0].imageUrl}
      onAdd={onAdd}
      onClick={() => onClick(mealID)}
      addEnabled={!!toRecipesBoard}
    />
  );
};

RecipeCollectionsAddMealItem.propTypes = {
  mealID: PropTypes.string.isRequired,
  destinationBoardID: PropTypes.string.isRequired,
  destinationMenuID: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

export default RecipeCollectionsAddMealItem;
